import React from 'react';
import PropTypes from 'prop-types';
import { Message } from 'mage-i18n';
import { Badge, Link, SpacerHorizontal, SpacerVertical, Typography } from '@klarna/bubble-ui';
import { Row, Column } from 'mage-components';
import { generateCyElemId } from 'moet-test-utils/cypress/utils';

import { REGIONS } from '@/constants';
import { STAKEHOLDER_ROLES_V2 } from '@/containers/withStakeholderLogic';
import * as personalIdUtils from '@/utils/personalId';
import * as dateUtils from '@/utils/date';
import { hasAddressData } from '@/utils/address';
import { CY_APPLICATION_CONFIRMATION_CONTAINER_IDS } from '@/containers/ApplicationConfirmation/constants';

import AddressSummary from './AddressSummary';
import SummaryColumn from './SummaryColumn';
import SummarySection from './SummarySection';

const tPrefix = 'products-fe.components.stakeholders.form';

const { NAME, DATE_OF_BIRTH, PERSONAL_ID } = CY_APPLICATION_CONFIRMATION_CONTAINER_IDS;

export const Header = ({ isRecollectionMerchant, t, title, roles, onEdit }) => {
  return (
    <>
      <Row>
        <Column>
          <div style={{ flexDirection: 'row', display: 'flex', lineHeight: 2 }}>
            <Typography textToken="text-style/text/labels/grande/bold">{title}</Typography>
            {onEdit && (
              <div style={{ paddingLeft: 10 }}>
                <Link
                  onPress={e => {
                    e.preventDefault();
                    onEdit();
                  }}
                  href=""
                >
                  <Message path={`${tPrefix}.edit`} />
                </Link>
              </div>
            )}
          </div>
        </Column>
      </Row>
      {roles?.length > 0 && (
        <Row>
          <Column>
            <div style={{ flexDirection: 'row', display: 'flex', lineHeight: 2 }}>
              {roles.map(role => (
                <React.Fragment key={`stakeholder-roles-${role}`}>
                  <Badge
                    backgroundColorToken="colors/backgrounds/subtle-inline"
                    borderColorToken="colors/backgrounds/subtle-inline"
                    textColorToken="colors/text/default"
                  >
                    {t(
                      `${
                        isRecollectionMerchant && role === STAKEHOLDER_ROLES_V2.APPLICANT
                          ? 'products-fe.containers.applicant.role.recollection'
                          : `products-fe.options.stakeholder.roles.${role}`
                      }`,
                    )}
                  </Badge>
                  <SpacerHorizontal spaceToken="space/100" />
                </React.Fragment>
              ))}
            </div>
          </Column>
        </Row>
      )}
    </>
  );
};

const PersonSummary = ({
  isRecollectionMerchant,
  merchantCountry,
  stakeholder: { hasPersonalId, firstName, lastName, dateOfBirth, address = {}, roles },
  onEdit,
  region,
  t,
}) => (
  <>
    <div {...generateCyElemId(NAME, window)}>
      <Header
        isRecollectionMerchant={isRecollectionMerchant}
        onEdit={onEdit}
        roles={region !== REGIONS.US ? roles : []}
        t={t}
        title={`${firstName} ${lastName}`}
      />
    </div>
    <SummarySection>
      <Row>
        <SummaryColumn
          titleKey={`${tPrefix}.dateOfBirth.label.without_format`}
          content={dateUtils.formatDate(merchantCountry, dateOfBirth)}
          {...generateCyElemId(DATE_OF_BIRTH, window)}
        />
        {hasPersonalId && (
          <SummaryColumn
            titleKey={`${tPrefix}.personal_id.${
              personalIdUtils.getField(region, merchantCountry, address.countryKey).translationKey
            }.label_summary`}
            content={t(`${tPrefix}.personal_id.value`)}
            {...generateCyElemId(PERSONAL_ID, window)}
          />
        )}
      </Row>
      {hasAddressData(address) ? (
        <AddressSummary address={address} forStakeholder />
      ) : (
        <SpacerVertical spaceToken="space/150" />
      )}
    </SummarySection>
  </>
);

PersonSummary.propTypes = {
  merchantCountry: PropTypes.string.isRequired,
  region: PropTypes.string.isRequired,
  stakeholder: PropTypes.shape({
    dateOfBirth: PropTypes.string.isRequired,
    firstName: PropTypes.string.isRequired,
    hasPersonalId: PropTypes.bool.isRequired,
    lastName: PropTypes.string.isRequired,
    roles: PropTypes.arrayOf(PropTypes.string).isRequired,
    address: PropTypes.shape({
      country: PropTypes.string.isRequired,
      countryKey: PropTypes.string.isRequired,
      city: PropTypes.string,
      postalCode: PropTypes.string,
      street: PropTypes.string,
    }),
  }).isRequired,
  t: PropTypes.func.isRequired,
  isRecollectionMerchant: PropTypes.bool,
  onEdit: PropTypes.func,
};

Header.propTypes = {
  roles: PropTypes.arrayOf(PropTypes.string).isRequired,
  t: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  isRecollectionMerchant: PropTypes.bool,
  onEdit: PropTypes.func,
};

export default PersonSummary;
